import { useCallback, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth } from '../state/AuthContext';
import { useRealtime, useResource } from '../state/hooks';
import { useSoftphone } from '../telephony/SoftphoneContext';
import { api } from '../lib/api';
import type { DemoStatus, LiveStats } from '../lib/types';
import { ConfirmButton } from './ui';

/**
 * The frame every console sits in: navigation filtered by permission, the live
 * operational counters, and — in the sandbox only — the demo controls.
 *
 * The counters are in the chrome rather than on one page on purpose. A
 * dispatcher who has drilled into an incident still needs to see that three
 * more callers are waiting.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const { user, org, isDemo, can, signOut } = useAuth();
  const navigate = useNavigate();
  const softphone = useSoftphone();
  const stats = useResource<{ stats: LiveStats }>('/reports/live');
  const demo = useResource<DemoStatus>(isDemo ? '/demo/status' : null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(() => stats.reload(), [stats]);
  const connection = useRealtime(['incidents', 'units', 'calls', 'messages', 'system'], refresh, refresh);

  if (!user) return null;
  const live = stats.data?.stats;

  const links = [
    { to: '/calls', label: 'Call taking', show: can('calls.handle') },
    { to: '/incidents', label: 'Incidents', show: can('incidents.read') },
    { to: '/dispatch', label: 'Dispatch', show: can('dispatch.view') },
    { to: '/field', label: 'Field', show: true },
    { to: '/reports', label: 'Reports', show: can('reports.own') },
    { to: '/admin', label: 'Admin', show: can('users.manage', 'system.config') },
  ].filter((link) => link.show);

  const simulateCall = async () => {
    setBusy(true);
    try {
      await api.post('/demo/simulate-call', {});
      softphone.refreshQueue();
    } finally {
      setBusy(false);
    }
  };

  const resetDemo = async () => {
    setBusy(true);
    try {
      await api.post('/demo/reset');
      stats.reload();
      softphone.refreshQueue();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="shell">
      <div>
        {isDemo && (
          <div className="demo-bar">
            <strong>Demo sandbox</strong>
            <span>Fictional data only. Calls are simulated and carry no audio.</span>
            {demo.data?.mockTelephony && (
              <span className="faint">
                {demo.data.mockTrunkRunning
                  ? `auto-generating a call about every ${demo.data.callIntervalSeconds}s`
                  : 'call generator paused'}
              </span>
            )}
            <div className="spacer" />
            {can('calls.handle') && (
              <button type="button" className="btn sm" disabled={busy} onClick={() => void simulateCall()}>
                Simulate inbound call
              </button>
            )}
            {can('system.config') && demo.data && (
              <button
                type="button"
                className="btn sm"
                disabled={busy}
                onClick={() =>
                  void api
                    .post('/demo/trunk', { running: !demo.data!.mockTrunkRunning })
                    .then(() => demo.reload())
                }
              >
                {demo.data.mockTrunkRunning ? 'Pause generator' : 'Resume generator'}
              </button>
            )}
            {can('demo.reset') && demo.data?.resetAllowed && (
              <ConfirmButton
                label="Reset sandbox"
                confirmLabel="Erase and re-seed?"
                className="btn danger sm"
                disabled={busy}
                onConfirm={() => void resetDemo()}
              />
            )}
          </div>
        )}

        <header className="topbar">
          <div className="brand">
            CAD<span>{org?.name ?? ''}</span>
          </div>

          <nav className="nav">
            {links.map((link) => (
              <NavLink key={link.to} to={link.to} className={({ isActive }) => (isActive ? 'active' : '')}>
                {link.label}
              </NavLink>
            ))}
          </nav>

          {live && (
            <div className="counters">
              <Counter value={live.calls_waiting} label="waiting" alert={live.calls_waiting > 0} />
              <Counter value={live.calls_active} label="on call" />
              <Counter value={live.pending_incidents} label="unassigned" alert={live.pending_incidents > 3} />
              <Counter value={live.active_incidents} label="active" />
              <Counter value={live.sla_breaches} label="SLA" alert={live.sla_breaches > 0} />
              <Counter value={live.units_available} label={`of ${live.units_on_duty} free`} />
            </div>
          )}

          <div className="topbar-right">
            <span
              className={`badge ${connection === 'open' ? 'ok' : connection === 'connecting' ? 'warn' : 'danger'}`}
              title={
                connection === 'open'
                  ? 'Live updates connected'
                  : 'Live updates are down — data on screen may be stale'
              }
            >
              <i className="dot" />
              {connection === 'open' ? 'Live' : connection === 'connecting' ? 'Connecting' : 'Offline'}
            </span>
            <div className="faint truncate" style={{ maxWidth: 180 }}>
              {user.fullName} · {user.role.replace('_', ' ')}
            </div>
            <button
              type="button"
              className="btn ghost sm"
              onClick={() => void signOut().then(() => navigate('/login'))}
            >
              Sign out
            </button>
          </div>
        </header>
      </div>

      <main className="main">{children}</main>
    </div>
  );
}

function Counter({ value, label, alert }: { value: number; label: string; alert?: boolean }) {
  return (
    <div className={`counter${alert ? ' alert' : ''}`}>
      <b>{value}</b>
      <span>{label}</span>
    </div>
  );
}
